@use "../../styles/layout.module.scss" as l;
@use "../../styles/breakpoint.module.scss" as *;

// 1024px–1439px: side cards tighten to 1 col each so the center card has room
$tablet-to-desktop: "min-width: #{$tablet}) and (max-width: #{$desktop - 1}";

.root {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;
    align-items: center;

    // Center card spans 8 cols + 7 gaps
    --center-width: calc(8 * var(--grid-column-width) + 7 * var(--grid-gap));
    --center-height: calc(var(--center-width) * 2 / 3);

    @media ($tablet-to-desktop) {
        --center-width: calc(
            10 * var(--grid-column-width) + 9 * var(--grid-gap)
        );
    }

    @media ($below-tablet) {
        --center-width: calc(
            var(--grid-column-count) * var(--grid-column-width) +
                (var(--grid-column-count) - 1) * var(--grid-gap)
        );
    }
}

// Decorative globe graphic — sits in its own row above the cards.
// Row 1 reserves only the portion visible above the cards (~40% of the SVG's
// natural rendered height); the SVG overflows downward behind the cards.
.graphic {
    grid-row: 1;
    grid-column: 2 / -2;
    aspect-ratio: 1344 / 192;
    pointer-events: none;
    color: var(--red);
    position: relative;
    inset-inline-start: -0.5px;

    @media ($below-tablet) {
        aspect-ratio: 1344 / 250;
    }

    @media ($below-mobile) {
        aspect-ratio: 1344 / 275;
    }

    svg {
        position: absolute;
        inset-block-start: 0;
        inset-inline-start: 0;
        width: 100%;
        height: auto;
    }
}

// Side cards — media only
// Height is 408/588 of the center card (design spec: center 588px, sides 408px at reference size)
.sideCard {
    overflow: hidden;
    grid-row: 2;
    height: calc(100% * 408 / 588);
    z-index: 1;
}

.leftCard {
    composes: sideCard;
    grid-column: 1 / col-end 2;
}

.rightCard {
    composes: sideCard;
    grid-column: col-start 11 / -1;
}

@media ($tablet-to-desktop) {
    .leftCard {
        grid-column: 1 / col-end 1;
    }

    .rightCard {
        grid-column: col-start 12 / -1;
    }
}

@media ($below-tablet) {
    .leftCard {
        grid-column: 1 / col-start 1;
    }

    .rightCard {
        grid-column: col-end 12 / -1;
    }
}

@media ($below-mobile) {
    .leftCard {
        grid-column: 1 / col-start 1;
    }

    .rightCard {
        grid-column: col-end 6 / -1;
    }
}

.centerCard {
    grid-row: 2;
    grid-column: col-start 3 / col-end 10;
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: var(--center-height);
    overflow: hidden;
    z-index: 1;

    @media ($tablet-to-desktop) {
        grid-column: col-start 2 / col-end 11;
    }

    @media ($below-tablet) {
        grid-column: content;
    }

    @media ($below-mobile) {
        grid-column: content;
        grid-template-columns: 1fr;
    }
}

// Text panel inside center card.
// Grid-stacks every slide's content in one cell so the panel sizes to the
// tallest slide and the carousel height stays stable across slides.
.textPanel {
    display: grid;
    grid-template-columns: 1fr;
    padding: var(--spacer-32);
    background-color: var(--color-primary);
    color: var(--color-text);
}

.textSlide {
    grid-column: 1;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    opacity: 0;
    transition: opacity var(--carousel-duration, 800ms) ease-in-out;
}

// On mobile, only the active (and any crossfading partner) participates in
// layout so the panel collapses to its natural height between transitions.
@media ($below-mobile) {
    .textSlide {
        display: none;

        &[data-active],
        &[data-partner] {
            display: flex;
        }
    }
}

// Media viewport & track — shared by all cards
.mediaViewport {
    cursor: grab;
    user-select: none;
    touch-action: pan-y;
    overflow: hidden;

    &[data-dragging] {
        cursor: grabbing;
    }
}

.centerCard .mediaViewport {
    position: relative;
    height: 100%;

    @media ($below-mobile) {
        order: -1;
        aspect-ratio: 1 / 1;
        height: auto;
    }
}

.sideCard .mediaViewport {
    height: 100%;
    cursor: default;
}

.mediaStack {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.stackSlide {
    position: absolute;
    inset: 0;
    will-change: transform;
}

.trackImage {
    width: 100%;
    height: 100%;
    display: block;
    max-width: none;
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
}

.stackSlide picture img,
.stackSlide video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.controls {
    display: flex;
    grid-row: 2;
    grid-column: 1 / col-end 12;
    align-self: end;
    justify-content: end;
    gap: 7px;

    > :first-child {
        transform: rotate(180deg);
    }

    @media ($below-tablet) {
        display: none;
    }
}

.dotsWrapper {
    composes: dotsWrapper from "../Clickables/dots.module.scss";
    grid-row: 3;
}

.dot {
    composes: dot from "../Clickables/dots.module.scss";
}
